import { useEffect, useState } from "react";
import API from "../../api/axiosConfig";
import { motion, AnimatePresence } from "framer-motion";
import {
  PlusCircle, Trash2, Edit, Calendar, ArrowLeft,
  BarChart2, Eye, EyeOff, Users, Trophy, ChevronDown, ChevronUp,
  Star, Hash, Download, Disc3,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../../utils/getImageUrl";
import EventRaffleModal from "../../components/admin/EventRaffleModal";

const MEDALS = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣"];

// ── Panel de resultados (inline) ─────────────────────────────
function ResultsPanel({ eventId }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [activeProduct, setActive]    = useState(null);
  const [voterMap, setVoterMap]       = useState({});
  const [voterLoading, setVL]         = useState(false);
  const [allParticipants, setAllParticipants] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    API.get(`/votes/${eventId}/results`)
      .then(res => setData(res.data?.data))
      .catch(() => toast.error("Error cargando resultados"))
      .finally(() => setLoading(false));
  }, [eventId]);

  async function fetchParticipants() {
    if (allParticipants) return allParticipants;
    const res = await API.get(`/votes/${eventId}/participants`);
    const list = res.data?.data?.participants ?? [];
    setAllParticipants(list);
    return list;
  }

  async function handleProductClick(productId) {
    if (activeProduct === productId) { setActive(null); return; }
    setActive(productId);
    if (voterMap[productId]) return;
    setVL(true);
    try {
      const participants = await fetchParticipants();
      const list = participants
        .filter(p => p.votes.some(v => v.product?.id === productId))
        .map(p => ({
          name:  p.user?.name ?? "—",
          email: p.user?.email ?? "",
          rank:  p.votes.find(v => v.product?.id === productId)?.voteRank ?? 0,
        }))
        .sort((a, b) => b.rank - a.rank);
      setVoterMap(prev => ({ ...prev, [productId]: list }));
    } catch { toast.error("Error cargando votantes"); }
    finally { setVL(false); }
  }

  async function handleDownload() {
    setDownloading(true);
    try {
      const participants = await fetchParticipants();
      const rows = [];
      for (const p of participants) {
        for (const v of p.votes) {
          const idx = data.results.findIndex(r => r.product?.id === v.product?.id);
          rows.push({
            posicion: idx >= 0 ? idx + 1 : "—",
            producto: v.product?.title ?? "",
            nombre:   p.user?.name ?? "",
            email:    p.user?.email ?? "",
            estrellas: v.voteRank,
          });
        }
      }
      rows.sort((a, b) => (a.posicion === "—" ? 99 : a.posicion) - (b.posicion === "—" ? 99 : b.posicion) || b.estrellas - a.estrellas);

      const esc = v => `"${String(v).replace(/"/g, '""')}"`;
      const header = "Posición,Producto,Nombre,Email,Estrellas";
      const lines  = rows.map(r => `${r.posicion},${esc(r.producto)},${esc(r.nombre)},${esc(r.email)},${r.estrellas}`);
      const csv    = "﻿" + [header, ...lines].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `resultados_${eventId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error generando descarga"); }
    finally { setDownloading(false); }
  }

  if (loading) return <p className="text-xs text-gray-400 py-3 text-center animate-pulse">Cargando resultados…</p>;
  if (!data?.results?.length) return <p className="text-xs text-gray-400 py-3 text-center">Sin votos aún.</p>;

  const maxStar = data.results[0]?.starSum ?? data.results[0]?.score ?? 1;

  return (
    <div className="mt-3 space-y-1">
      <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
        <span className="flex items-center gap-1"><Users size={12} /> {data.totalVotes} votos totales</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><Star size={12} className="text-amber-400" /> {(data.totalStarSum ?? data.totalScore).toLocaleString()} ⭐ totales</span>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-1 text-xs text-supernova-600 hover:text-supernova-700 font-medium disabled:opacity-50 transition-colors"
          >
            <Download size={12} />
            {downloading ? "Generando…" : "Descargar CSV"}
          </button>
        </div>
      </div>

      {data.results.map((r, idx) => {
        const pid      = r.product?.id ?? idx;
        const expanded = activeProduct === pid;
        const pct      = Math.round(((r.starSum ?? r.score) / maxStar) * 100);
        const voters   = voterMap[pid];

        return (
          <div key={pid}>
            {/* Fila del producto — clickeable */}
            <div
              onClick={() => handleProductClick(pid)}
              className="flex items-center gap-3 cursor-pointer rounded-xl px-2 py-1.5 hover:bg-gray-50 transition-colors select-none"
            >
              <span className="text-lg w-6 shrink-0">{MEDALS[idx] ?? `${idx + 1}.`}</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-800 truncate">{r.product?.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                    <div className="h-1.5 rounded-full bg-supernova-400 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs text-gray-400 shrink-0">{r.votes}v</span>
                  <span className="text-xs font-bold text-amber-500 shrink-0">⭐ {(r.starSum ?? r.score).toLocaleString()}</span>
                </div>
              </div>
              <span className="text-gray-300 shrink-0">{expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}</span>
            </div>

            {/* Panel de votantes */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-hidden"
                >
                  <div className="ml-9 mr-1 mb-2 rounded-xl border border-gray-100 bg-gray-50 overflow-hidden">
                    {voterLoading && !voters ? (
                      <p className="text-[11px] text-gray-400 text-center py-3 animate-pulse">Cargando votantes…</p>
                    ) : !voters?.length ? (
                      <p className="text-[11px] text-gray-400 text-center py-3">Sin votantes.</p>
                    ) : (
                      <div className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
                        {voters.map((v, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[11px] font-semibold text-gray-800 truncate">{v.name}</p>
                              <p className="text-[10px] text-gray-400 truncate">{v.email}</p>
                            </div>
                            <div className="flex items-center gap-0.5 shrink-0">
                              {[1,2,3,4,5].map(n => (
                                <Star key={n} size={11}
                                  fill={n <= v.rank ? "#f59e0b" : "none"}
                                  stroke={n <= v.rank ? "#f59e0b" : "#d1d5db"}
                                  strokeWidth={1.5}
                                />
                              ))}
                              <span className="text-[10px] font-bold text-amber-600 ml-1">{v.rank}/5</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

// ── Panel de participantes (inline) ──────────────────────────
function ParticipantsPanel({ eventId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");

  useEffect(() => {
    API.get(`/votes/${eventId}/participants`)
      .then(res => setData(res.data?.data))
      .catch(() => toast.error("Error cargando participantes"))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <p className="text-xs text-gray-400 py-3 text-center animate-pulse">Cargando participantes…</p>;
  if (!data?.participants?.length) return <p className="text-xs text-gray-400 py-3 text-center">Sin participantes aún.</p>;

  const isStars = data.votingType === "stars";

  const filtered = search.trim()
    ? data.participants.filter(p =>
        p.user?.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.user?.email?.toLowerCase().includes(search.toLowerCase())
      )
    : data.participants;

  return (
    <div className="mt-3 space-y-3">
      {/* Resumen */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1"><Users size={12} /> {data.totalParticipants} participantes</span>
        <span className="flex items-center gap-1"><Hash size={12} /> {data.totalVotes} calificaciones</span>
        <span className="flex items-center gap-1">
          {isStars ? <Star size={12} className="text-amber-400" /> : <Trophy size={12} />}
          {isStars ? "Modo estrellas" : "Modo ranking"}
        </span>
      </div>

      {/* Buscador */}
      <input
        type="text"
        placeholder="Buscar por nombre o email…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full text-xs px-3 py-1.5 border border-gray-200 rounded-lg outline-none focus:border-supernova-400"
      />

      {/* Tabla de participantes */}
      <div className="rounded-xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-[1fr_auto] bg-gray-50 px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">
          <span>Usuario</span>
          <span className="text-right">Calificaciones</span>
        </div>

        {filtered.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">Sin resultados para la búsqueda.</p>
        ) : (
          <div className="divide-y divide-gray-50 max-h-72 overflow-y-auto">
            {filtered.map((p, i) => (
              <div key={p.user?.id ?? i} className="px-3 py-2.5">
                {/* Datos del usuario */}
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{p.user?.name ?? "—"}</p>
                    <p className="text-[10px] text-gray-400 truncate">{p.user?.email ?? ""}</p>
                  </div>
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0 font-medium">
                    {p.votes.length} {p.votes.length === 1 ? "voto" : "votos"}
                  </span>
                </div>

                {/* Lista de productos calificados */}
                <div className="space-y-1">
                  {p.votes.map((v, j) => (
                    <div key={j} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-2 py-1">
                      <p className="text-[11px] text-gray-700 truncate flex-1">{v.product?.title ?? "Producto"}</p>
                      {isStars ? (
                        <div className="flex items-center gap-0.5 shrink-0">
                          {[1,2,3,4,5].map(n => (
                            <Star
                              key={n}
                              size={11}
                              fill={n <= v.voteRank ? "#f59e0b" : "none"}
                              stroke={n <= v.voteRank ? "#f59e0b" : "#d1d5db"}
                              strokeWidth={1.5}
                            />
                          ))}
                          <span className="text-[10px] text-amber-600 font-bold ml-1">{v.voteRank}/5</span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-supernova-600 shrink-0">
                          {MEDALS[v.voteRank - 1] ?? `#${v.voteRank}`}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tarjeta de evento ─────────────────────────────────────────
function EventCard({ ev, onEdit, onDelete, onToggleActive, onTogglePublish }) {
  const [showResults,      setShowResults]      = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showRaffle,       setShowRaffle]       = useState(false);

  return (
    <motion.div
      whileHover={{ scale: 1.005 }}
      className="bg-white rounded-2xl shadow-md border overflow-hidden"
    >
      {ev.bannerBase64 && (() => {
        const src = ev.bannerBase64.startsWith("data:")
          ? ev.bannerBase64
          : getImageUrl(ev.bannerBase64);
        return <img src={src} alt="banner" className="w-full h-40 object-cover" />;
      })()}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="text-lg font-bold text-gray-800 truncate">{ev.title}</h4>
            <p className="text-sm text-gray-500 line-clamp-2 mt-0.5">{ev.description}</p>
          </div>
          <span className={`shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${
            ev.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"
          }`}>
            {ev.active ? "Activo" : "Inactivo"}
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
          <Calendar size={13} />
          {ev.startDate?.substring(0, 10)} → {ev.endDate?.substring(0, 10)}
        </div>

        {/* Config badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            🗳️ {ev.maxVotesPerDevice ?? 1} voto{(ev.maxVotesPerDevice ?? 1) > 1 ? "s" : ""}/dispositivo
          </span>
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">
            👥 {ev._count?.votes ?? 0} votos
          </span>
          {ev.votingType === "stars" && (ev.totalStars ?? 0) > 0 && (
            <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">
              ⭐ {(ev.totalStars ?? 0).toLocaleString()} estrellas
            </span>
          )}
          {ev.publishResults
            ? <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">✅ Resultados públicos</span>
            : <span className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full font-medium">🔒 Resultados privados</span>
          }
        </div>

        {/* Acciones */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => onEdit(ev)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-semibold hover:bg-blue-600 transition">
            <Edit size={13} /> Editar
          </button>
          <button onClick={() => onToggleActive(ev.id, ev.active)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition ${
              ev.active ? "bg-amber-500 hover:bg-amber-600" : "bg-green-500 hover:bg-green-600"
            }`}>
            {ev.active ? "Desactivar" : "Activar"}
          </button>
          <button onClick={() => onTogglePublish(ev.id, ev.publishResults)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition ${
              ev.publishResults ? "bg-orange-500 hover:bg-orange-600" : "bg-purple-600 hover:bg-purple-700"
            }`}>
            {ev.publishResults ? <><EyeOff size={13} /> Ocultar resultados</> : <><Eye size={13} /> Publicar resultados</>}
          </button>
          <button onClick={() => { setShowResults(v => !v); setShowParticipants(false); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-semibold hover:bg-gray-200 transition">
            <BarChart2 size={13} />
            {showResults ? <><ChevronUp size={13} /> Ocultar</> : <><ChevronDown size={13} /> Ver resultados</>}
          </button>
          <button onClick={() => { setShowParticipants(v => !v); setShowResults(false); }}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              showParticipants
                ? "bg-indigo-500 text-white hover:bg-indigo-600"
                : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            }`}>
            <Users size={13} />
            {showParticipants ? <><ChevronUp size={13} /> Ocultar</> : <><ChevronDown size={13} /> Ver participantes</>}
          </button>
          {(ev._count?.votes ?? 0) > 0 && (
            <button onClick={() => setShowRaffle(true)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition">
              <Disc3 size={13} /> Sortear ganador
            </button>
          )}
          <button onClick={() => onDelete(ev.id)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition ml-auto">
            <Trash2 size={13} /> Eliminar
          </button>
        </div>

        {/* Panel de resultados expandible */}
        <AnimatePresence>
          {showResults && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t">
                <ResultsPanel eventId={ev.id} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Panel de participantes expandible */}
        <AnimatePresence>
          {showParticipants && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs font-bold text-gray-600 mb-2 flex items-center gap-1.5">
                  <Users size={13} className="text-indigo-500" /> Participantes y calificaciones
                </p>
                <ParticipantsPanel eventId={ev.id} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showRaffle && (
        <EventRaffleModal eventId={ev.id} eventTitle={ev.title} onClose={() => setShowRaffle(false)} />
      )}
    </motion.div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function EventManager() {
  const [events, setEvents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await API.get("/events");
      setEvents(res.data?.data?.events ?? res.data?.data ?? []);
    } catch { toast.error("Error cargando eventos"); }
  };

  const handleEdit = (ev) => { navigate(`/events/${ev.id}/edit`); };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este evento? Se borrarán todos sus votos.")) return;
    try {
      await API.delete(`/events/${id}`);
      toast.success("Evento eliminado");
      fetchEvents();
    } catch { toast.error("Error eliminando evento"); }
  };

  const handleToggleActive = async (id, current) => {
    try {
      await API.patch(`/events/${id}/toggle`);
      toast.success(current ? "Evento desactivado" : "Evento activado");
      fetchEvents();
    } catch { toast.error("Error cambiando estado"); }
  };

  const handleTogglePublish = async (id, current) => {
    try {
      await API.patch(`/events/${id}/publish-results`, { publish: !current });
      toast.success(!current ? "✅ Resultados publicados al público" : "🔒 Resultados ocultados");
      fetchEvents();
    } catch { toast.error("Error publicando resultados"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-supernova-50 to-supernova-100 p-6 md:p-10">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-supernova-600 transition mb-6">
          <ArrowLeft size={18} /> Volver
        </button>

        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">Gestión de Eventos</h3>
          <button
            onClick={() => navigate("/events/new")}
            className="bg-supernova-500 px-4 py-2 text-white rounded-xl flex items-center gap-2 hover:bg-supernova-600 transition font-semibold"
          >
            <PlusCircle size={20} /> Nuevo Evento
          </button>
        </div>

        {events.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Calendar size={48} className="mx-auto mb-3 opacity-40" />
            <p className="font-medium">No hay eventos creados</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.map(ev => (
              <EventCard
                key={ev.id}
                ev={ev}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
                onTogglePublish={handleTogglePublish}
                onRefresh={fetchEvents}
              />
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
